import { useState } from 'react'
import { usePrimaryPage } from '@/PageManager'
import { unwrappedFeedAtom } from '@/atoms/feed'
import { switchFeed } from '@/PageManager'
import { hideFollowingAtom } from '@/atoms/preferences'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  Contact01Icon,
  Tag01Icon
} from '@hugeicons/core-free-icons'
import SidebarItem from './SidebarItem'
import { useCurrentPubkey } from '@/atoms/session'
import { useAtomValue } from 'jotai'
import { unwrappedFavoriteFollowListsWithSetsAtom, unwrappedFollowListAtom } from '@/atoms/lists'

import { ResolvedSet } from '@nostr/gadgets/sets'

export default function FollowingButton({ collapse }: { collapse: boolean }) {
  const { navigate, current, display } = usePrimaryPage()
  const feed = useAtomValue(unwrappedFeedAtom)
  const pubkey = useCurrentPubkey()
  const hideFollowing = useAtomValue(hideFollowingAtom)
  const followList = useAtomValue(unwrappedFollowListAtom)
  const favoriteFollowSets = useAtomValue(unwrappedFavoriteFollowListsWithSetsAtom)

  const [showAll, setShowAll] = useState(false)

  if (hideFollowing) return null

  if (!followList?.length && !favoriteFollowSets?.length) return null

  const mainItem = favoriteFollowSets?.[0]
  const otherItems = favoriteFollowSets?.slice(1) ?? []
  const displayedItems = showAll ? otherItems : otherItems.slice(0, 3)
  const hasMore = otherItems.length > 3

  return (
    <div>
      <SidebarItem
        title="Following"
        onClick={() => {
          navigate('home')
          switchFeed({ pubkey: pubkey! })
        }}
        active={display && current === 'home' && feed && 'pubkey' in feed}
        collapse={collapse}
      >
        <HugeiconsIcon icon={Contact01Icon} />
      </SidebarItem>
      {!collapse && favoriteFollowSets && favoriteFollowSets.length > 0 && (
        <div className="flex flex-col items-end gap-px mt-px pr-1">
          {mainItem && (
            <FollowSetSubItem
              key={mainItem.pointer.pubkey + mainItem.pointer.identifier}
              set={mainItem}
              onClick={() => setShowAll(false)}
            />
          )}
          {displayedItems.map((item) => (
            <FollowSetSubItem
              key={item.pointer.pubkey + item.pointer.identifier}
              set={item}
              onClick={() => setShowAll(false)}
            />
          ))}
          {hasMore && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="flex items-center justify-center w-full text-muted-foreground"
            >
              {showAll ? (
                <HugeiconsIcon icon={ChevronUpIcon} className="size-3" />
              ) : (
                <HugeiconsIcon icon={ChevronDownIcon} className="size-3" />
              )}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function FollowSetSubItem({ set, onClick }: { set: ResolvedSet<string>; onClick: () => void }) {
  const { navigate, current, display } = usePrimaryPage()
  const feed = useAtomValue(unwrappedFeedAtom)
  const active =
    display &&
    current === 'home' &&
    feed &&
    'followSet' in feed &&
    feed.followSet.pointer.identifier === set.pointer.identifier &&
    feed.followSet.pointer.pubkey === set.pointer.pubkey

  return (
    <button
      onClick={() => {
        onClick()
        navigate('home')
        switchFeed({
          followSet: set
        })
      }}
      className={`flex items-center gap-1.5 w-5/6 px-2 py-1 rounded text-xs truncate transition-colors ${
        active
          ? 'text-primary bg-primary/10 hover:bg-primary/10'
          : 'text-muted-foreground hover:bg-accent/40'
      }`}
    >
      <HugeiconsIcon icon={Tag01Icon} className="w-4 h-4 shrink-0" />
      <span className="truncate">
        {set.title} ({set.items.length})
      </span>
    </button>
  )
}
